import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect, useSelector } from 'react-redux'
import { fromZonedTime } from 'date-fns-tz'
import { endOfDay } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { resetQueries } from '@/utils/api'
import { GET_CHECKLISTS_BASE_QUERY_KEY } from '@/api/checklists/get-checklists'
import { GET_CHECKLIST_TASK_DETAILS_BASE_QUERY_KEY } from '@/api/checklists/get-checklist-task-details'

import api from '../../../../api/api'
import {
  getClients,
  selectClients,
  selectStatusIsLoadedAll,
  setLoadingState,
} from '../../../../reducers/clientsReducer'
import {
  fetchSingleBuilding,
  selectActiveBuilding,
} from '../../../../reducers/buildingSelectorReducer'
import { mapClientToUser, getNamedHandler } from '../helpers'
import ModalContainer from '../../../../components/ModalContainer/ModelContainer'
import Icon from '../../../../components/Icons'
import SelectDropdownInput from '../../../../components/SelectDropdownInput/SelectDropdownInput'
import DateField from '../../../../components/FormLegacy/FormFieldsV2/DateField'
import Filter from '../../../../components/Filter/Filter'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import { TextInput } from '@/components/ui/text-input'

const ChecklistAssignTaskModal = ({
  users,
  allClientsLoaded,
  // Note: pre-selects and locks the resident field - used when assigning a task from a
  // resident's own profile, where the resident is implied by context and must not be changed.
  lockResident = undefined,
  // Note: when set, the resident dropdown lists only these options ({ value, label }) instead of
  // every client of the building - used when assigning from a unit, limited to its residents.
  residentOptions = undefined,
  // Note: overrides the globally-selected building (Redux) - needed so a resident's own
  // building is used even if a PM has a different building selected in the top nav.
  building = undefined,
  onClose = () => {},
  showCreateNewTask = () => {},
  getClients = () => {},
  setLoadingState = () => {},
  fetchSingleBuilding = () => {},
}) => {
  const { t } = useTranslation(['checklists-tasks', 'common'])
  const globalActiveBuilding = useSelector(selectActiveBuilding)
  const activeBuilding = building ?? globalActiveBuilding
  const [isLoading, setIsLoading] = useState(false)
  const [tasks, setTasks] = useState([])
  const [formState, setFormState] = useState({
    selectedTasks: [],
    dueDate: null,
    client: lockResident ? { value: lockResident.id, label: lockResident.name } : null,
  })
  const { displaySnackbar } = useSnackbar()

  const handleFieldChange = ({ target: { name, value } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const handleSearchSelect = getNamedHandler(handleFieldChange)

  const fetchClients = async (query = '', isScrolling = false, buildingId = activeBuilding.id) => {
    const limit = 15
    const offset =
      users.length % limit === 0
        ? users.length / limit
        : (users.length - (users.length % limit)) / limit

    await getClients(isScrolling ? offset + 1 : 1, buildingId, undefined, query, limit)
  }

  const fetchCustomTasks = async () => {
    try {
      const result = await api.getShortCustomTasksList(activeBuilding.id)
      setTasks(result.data.map((task) => ({ ...task, text: task.name })))
    } catch ({ message }) {
      console.error(message)
    }
  }

  useEffect(() => {
    if (activeBuilding) {
      if (!lockResident && !residentOptions) fetchClients()
      fetchCustomTasks()
    }
  }, [activeBuilding])

  const onCreateNewTask = () => {
    onClose()
    showCreateNewTask()
  }

  const onSubmitFinally = async () => {
    if (activeBuilding.id) {
      await fetchSingleBuilding(activeBuilding.id)
    }
    setIsLoading(false)
    onClose()
  }

  const onSubmit = async () => {
    setIsLoading(true)
    const selectedTasksIds = formState.selectedTasks.map((task) => task.id)
    const selectedTasksNames = formState.selectedTasks.map((task) => task.text).join(', ')
    const requestData = {
      clientId: formState.client.value,
      checklistIds: selectedTasksIds,
      ...(formState.dueDate
        ? { dueDate: fromZonedTime(endOfDay(formState.dueDate), 'UTC').getTime() }
        : {}),
    }

    try {
      await api.assignChecklist(requestData).then(() => {
        displaySnackbar(
          t('checklists-tasks:assignModal.tasksAssignedSuccess', {
            tasks: selectedTasksNames,
            client: formState.client.label,
          }),
        )
      })
    } catch ({ message, response }) {
      console.error(message)
      displaySnackbar(
        (response.data && response.data.message) ||
          t('checklists-tasks:assignModal.errorAssigningTask'),
        true,
      )
    } finally {
      // Note: Invalidate queries to get fresh data
      // This modal is used with new standards, that's why we have to use tanstack query here
      await resetQueries(
        [GET_CHECKLISTS_BASE_QUERY_KEY],
        [GET_CHECKLIST_TASK_DETAILS_BASE_QUERY_KEY],
      )
      onSubmitFinally()
    }
  }

  const isReady = activeBuilding && formState.selectedTasks.length && formState.client

  return (
    <ModalContainer
      additionalClass={'checklist-modal'}
      onClose={onClose}
      headerTitle={t('checklists-tasks:assignModal.assignTaskLabel')}
      headerIcon={<Icon icon="checkList" />}
    >
      <div className="checklist-modal-content checklist-assign-task">
        <div className="checklist-modal-input-row">
          <div className="input-container">
            <div className="input-label">
              {t('checklists-tasks:assignModal.selectResidentLabel')}
            </div>
            {lockResident ? (
              <TextInput disabled readOnly value={lockResident.name} />
            ) : (
              <SelectDropdownInput
                placeholder={t('checklists-tasks:assignModal.searchResidentPlaceholder')}
                data={residentOptions ?? users}
                onSelectOption={(item) => handleSearchSelect(item, 'client')}
                fetchData={(skip, query, isScrolling, buildingId) =>
                  residentOptions ? undefined : fetchClients(query, isScrolling, buildingId)
                }
                isLoadedAll={residentOptions ? true : allClientsLoaded}
                setLoadingState={residentOptions ? undefined : setLoadingState}
                dataToRefetch={activeBuilding.id}
                showSelectedInDropdown
              />
            )}
          </div>
        </div>
        <div className="checklist-modal-input-row">
          <div className="input-container">
            <div className="input-label">{t('checklists-tasks:assignModal.selectTaskLabel')}</div>
            <Filter
              placeholder={
                formState.selectedTasks.length
                  ? formState.selectedTasks.map((task) => task.text).join(', ')
                  : t('checklists-tasks:assignModal.selectDefaultPlaceholder')
              }
              options={tasks}
              value={formState.selectedTasks}
              config={{ hasSearch: true, multiple: true }}
              name="selectedTasks"
              onChange={(e) => {
                e.target.value && handleSearchSelect(e.target.value, 'selectedTasks')
              }}
              additionalClass="checklist-assign-task__filter"
            />
            <span
              className="input-desc-label label-primary"
              style={{ cursor: 'pointer', width: 'max-content' }}
              onClick={onCreateNewTask}
            >
              {t('checklists-tasks:assignModal.createNewTaskLink')}
            </span>
          </div>
        </div>
        <div className="checklist-modal-input-row">
          <div className="input-container">
            <div className="input-label">
              {t('checklists-tasks:assignModal.dueDateLabel')}{' '}
              <span style={{ fontSize: 12 }}>
                {t('checklists-tasks:assignModal.optionalSuffix')}
              </span>
            </div>
            <DateField
              name="dueDate"
              placeholderText={t('checklists-tasks:assignModal.dueDatePlaceholder')}
              autoComplete="off"
              dateFormat="MM/dd/yyyy"
              minDate={new Date()}
              value={formState.dueDate}
              onChange={handleFieldChange}
              onChangeRaw={(e) => e.preventDefault()}
            />
          </div>
        </div>
      </div>
      <div className="checklist-modal-actions">
        <button className="button button-gray" onClick={onClose}>
          {t('common:actions.cancel')}
        </button>
        <button
          className="button button-primary auto-width"
          disabled={!isReady || isLoading}
          onClick={onSubmit}
        >
          {t('checklists-tasks:assignModal.assignTaskLabel')}
        </button>
      </div>
    </ModalContainer>
  )
}

ChecklistAssignTaskModal.propTypes = {
  users: PropTypes.array,
  allClientsLoaded: PropTypes.bool,
  lockResident: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    apartment: PropTypes.number,
    buildingNumber: PropTypes.string,
  }),
  residentOptions: PropTypes.array,
  building: PropTypes.object,
  onClose: PropTypes.func,
  showCreateNewTask: PropTypes.func,
  getClients: PropTypes.func,
  setLoadingState: PropTypes.func,
  fetchSingleBuilding: PropTypes.func,
}

export default connect(
  (state) => ({
    users: selectClients(state).map(mapClientToUser),
    allClientsLoaded: selectStatusIsLoadedAll(state),
  }),
  {
    getClients,
    setLoadingState,
    fetchSingleBuilding,
  },
)(ChecklistAssignTaskModal)
